import React, { useState, useEffect } from "react";
import styled from "styled-components/macro";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import Typography from "@material-ui/core/Typography";

import BorrowingEntitiesTooltip from "./borrowingEntitiesTooltip";
import { LENDER_REPORTING_DASHBOARD } from "constants/routes";

const AssigneeAndFilterWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const BorrowingEntities = styled.div`
  font-family: Work Sans;
  font-size: 16px;
  font-weight: 400;
  line-height: 19px;
  color: #005aa9;
`;

const ButtonsExpirationWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 2em;
`;

const ButtonText = styled.div`
  font-family: Work Sans;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 19px;
`;

const ButtonWrapper = styled.div`
  && {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 19px;
    font-family: Work Sans;
    font-style: normal;
    font-weight: 500;
    font-size: 16px;
    line-height: 19px;
    color: #005aa9;
    text-decoration: none;
    cursor: pointer;
  }
`;

const FilterWrapper = styled.div`
  padding-bottom: 20px;
  display: flex;
  align-items: center;
`;

const DealName = styled.div`
  font-family: Work Sans;
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 19px;
  color: #000000;
`;

const DealNameFlexRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.5em;
`;

const ReportName = styled.div`
  font-family: Work Sans;
  font-style: normal;
  font-weight: normal;
  font-size: 28px;
  line-height: 33px;
  display: flex;
  align-items: center;
  color: #000000;
`;

const TitleApproveWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const TitleSubtitleWrapper = styled.div`
  height: 70%;
  display: flex;
  flex-direction: column;
  gap: 1em;
  justify-content: center;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid #dadce0;
  width: calc(100% - (2em + 1px));
  background-color: #ffffff;
  padding: 4em 1em 0em 1em;
`;

const StyledTypography = styled(Typography)`
  && {
    font-family: "Work Sans";
    font-style: normal;
    font-weight: ${({ bolder }) => (bolder ? "500" : "400")};
    font-size: 16px;
    line-height: 19px;
    color: ${({ gray }) => gray && "#5F6368"};
  }
`;

const ReportingYearLabel = styled(InputLabel)`
  && { 
    margin-right: 5px;
    font-family: work sans;
    color: #000;
    font-weight: 400;
    padding-bottom: 1px;
  }
`;

const YearSubmittedSelect = styled(Select)`
  && { 
    color: #005AA9;
    font-family: work sans;
  }
`;

const removeDuplicateBorrowEnts = tasks => tasks.reduce((uniqueBorrowEnts, task) => {
  task.deal.borrowingEntities.forEach(bE => {
    if (!uniqueBorrowEnts.includes(bE)) {
      uniqueBorrowEnts.push(bE);
    }
  });

  return uniqueBorrowEnts;
}, []);

const SubmittedReportHeader = ({ data, history, filterYears, yearFilter, setYearFilter }) => {
  const { tasks } = data;
  const borrowingEntities = tasks && tasks.length > 0 ? removeDuplicateBorrowEnts(tasks).map(borrowingEntity => <div>{borrowingEntity}</div>) : [];
  const [shortenedReportName, setShortenedReportName] = useState("");
  const fullReportName = data.reportGroup?.reportName;
  const [assetManagerName, setAssetManagerName] = useState("");
  const assetManager = data.deal?.assetManager;

  const handleChange = event => {
    setYearFilter(event.target.value);
  };

  useEffect(() => {
    if (fullReportName) {
      const splitReportName = fullReportName.split(" - ");
      const trimmedReportName = splitReportName[2].trim();
      setShortenedReportName(trimmedReportName);
    }
  }, [fullReportName]);

  useEffect(() => {
    if (assetManager) {
      setAssetManagerName(`${assetManager.firstName} ${assetManager.lastName}`);
    }
  }, [assetManager]);

  const getLinkText = prevLocation => (prevLocation === "direct-from-dashboard" ? "Back to Dashboard" : "Back to current report");
  const getRouteFromHistory = prevLocation => (prevLocation === "direct-from-dashboard" ? history.push(LENDER_REPORTING_DASHBOARD) :
    history.push(`/lenderReportingDashboard/tasks/${data?.reportGroupInstance?.Items[0].id}`, { from: history.location.state?.from }));

  return (
    <Wrapper>
      <ButtonsExpirationWrapper>
        <ButtonWrapper onClick={() => getRouteFromHistory(history.location.state?.from)}>
          <div className="material-icons" style={{ width: "24px" }}>
            arrow_backwards
          </div>
          <ButtonText>{getLinkText(history.location.state?.from)}</ButtonText>
        </ButtonWrapper>
      </ButtonsExpirationWrapper>
      <TitleApproveWrapper>
        <TitleSubtitleWrapper>
          <DealNameFlexRow>
            <ButtonWrapper onClick={() => history.push(`/deal/${data.deal.id}`)}>
              <div className="material-icons">open_in_new</div>
            </ButtonWrapper>
            <DealName>{data.deal?.name}</DealName>
            <BorrowingEntitiesTooltip entities={borrowingEntities}>
              <BorrowingEntities>(See borrowing entities)</BorrowingEntities>
            </BorrowingEntitiesTooltip>
          </DealNameFlexRow>
          <ReportName>{shortenedReportName}</ReportName>
        </TitleSubtitleWrapper>
      </TitleApproveWrapper>
      <AssigneeAndFilterWrapper>
        <StyledTypography>
          Assigned to
          {" "}
          <b>{assetManagerName ? assetManagerName : <></>}</b>
        </StyledTypography>
        <FilterWrapper>
          <ReportingYearLabel>
            Reporting year:
          </ReportingYearLabel>
          <FormControl>
            <YearSubmittedSelect
              SelectDisplayProps={{ style: { paddingLeft: "5px" } }}
              IconComponent={ExpandMoreIcon}
              disableUnderline
              value={yearFilter}
              onChange={handleChange}
              defaultValue={"All"}
              displayEmpty
              name="year"
            >
              {filterYears.map(year => (
                <MenuItem value={year} key={year + 1}>
                  {year}
                </MenuItem>
              ))}
            </YearSubmittedSelect>
          </FormControl>
        </FilterWrapper>
      </AssigneeAndFilterWrapper>
    </Wrapper>
  );
};

export default SubmittedReportHeader;
